/**
 * Session Timeout Hook
 *
 * Hook for managing session timeout with automatic warnings and expiration
 *
 * Features:
 * - Tracks session timeout based on user settings
 * - Shows warning modal 2 minutes before expiration
 * - Automatically signs out on expiration
 * - Allows session extension
 * - Resets timer on user activity (paused while warning is showing)
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSecuritySettings } from './useSecuritySettings';
import { securityService } from '../services/security-service';
import { supabaseAuthService } from '../services/supabase-service';
import { getCurrentUserId } from '../lib/supabase';
import {
  createSessionTimer,
  clearSessionTimer,
  calculateExpirationTime,
  isSessionExpired,
} from '../utils/session-timeout-utils';

interface UseSessionTimeoutOptions {
  onWarning?: (timeRemainingMinutes: number) => void;
  onExpire?: () => void;
  enabled?: boolean;
}

export function useSessionTimeout(options: UseSessionTimeoutOptions = {}) {
  const { onWarning, onExpire, enabled = true } = options;
  const navigate = useNavigate();
  const { data: securitySettings } = useSecuritySettings();

  const timerRef = useRef<{ clear: () => void } | null>(null);
  const expirationTimeRef = useRef<Date | null>(null);
  const lastActivityRef = useRef<Date>(new Date());
  const isWarningActiveRef = useRef(false);
  const isExpiringRef = useRef(false);

  const timeoutMinutes = securitySettings?.sessionTimeoutMinutes ?? 30;

  // Handle session expiration — always sign out so auth state matches the redirect
  const handleExpire = useCallback(async () => {
    if (isExpiringRef.current) return;
    isExpiringRef.current = true;
    isWarningActiveRef.current = false;

    if (timerRef.current) {
      clearSessionTimer(timerRef.current);
      timerRef.current = null;
    }

    try {
      await supabaseAuthService.signOut();
    } catch (error) {
      console.error('Error signing out on session timeout:', error);
    }

    if (onExpire) {
      onExpire();
    } else {
      navigate('/login?sessionExpired=true', { replace: true });
    }
  }, [onExpire, navigate]);

  // Handle session warning
  const handleWarning = useCallback(
    (timeRemainingMinutes: number) => {
      isWarningActiveRef.current = true;
      if (onWarning) {
        onWarning(timeRemainingMinutes);
      }
    },
    [onWarning]
  );

  // Extend session
  const extendSession = useCallback(async () => {
    try {
      const userId = await getCurrentUserId();
      if (!userId) return false;

      const result = await securityService.extendSession(userId);
      if (result.error) {
        console.error('Failed to extend session:', result.error);
        return false;
      }

      isWarningActiveRef.current = false;
      lastActivityRef.current = new Date();

      if (result.data?.newExpirationTime) {
        expirationTimeRef.current = result.data.newExpirationTime;
      } else {
        expirationTimeRef.current = calculateExpirationTime(timeoutMinutes);
      }

      if (timerRef.current) {
        clearSessionTimer(timerRef.current);
      }

      timerRef.current = createSessionTimer(timeoutMinutes, handleWarning, () => {
        void handleExpire();
      });

      return true;
    } catch (error) {
      console.error('Error extending session:', error);
      return false;
    }
  }, [timeoutMinutes, handleWarning, handleExpire]);

  // Reset timer on user activity
  const resetTimer = useCallback(() => {
    if (!securitySettings || !enabled || isWarningActiveRef.current || isExpiringRef.current) {
      return;
    }

    lastActivityRef.current = new Date();

    if (timerRef.current) {
      clearSessionTimer(timerRef.current);
    }

    expirationTimeRef.current = calculateExpirationTime(timeoutMinutes);

    timerRef.current = createSessionTimer(timeoutMinutes, handleWarning, () => {
      void handleExpire();
    });
  }, [securitySettings, enabled, timeoutMinutes, handleWarning, handleExpire]);

  const hasSecuritySettings = Boolean(securitySettings);

  // Initialize timer when settings load or timeout duration changes.
  // Depend on timeoutMinutes (primitive), not the settings object, so React Query
  // refetches do not continually reset the inactivity timer.
  useEffect(() => {
    if (!hasSecuritySettings || !enabled) return;

    isExpiringRef.current = false;
    isWarningActiveRef.current = false;
    resetTimer();

    return () => {
      if (timerRef.current) {
        clearSessionTimer(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetTimer excluded to avoid churn
  }, [timeoutMinutes, enabled, hasSecuritySettings]);

  // Track user activity (mouse movements, clicks, keyboard input)
  useEffect(() => {
    if (!enabled) return;

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      // While the warning is showing, only explicit "Extend Session" should reset
      if (isWarningActiveRef.current || isExpiringRef.current) return;

      const now = new Date();
      const timeSinceLastActivity = now.getTime() - lastActivityRef.current.getTime();

      // Reset timer if more than 1 minute has passed since last activity
      if (timeSinceLastActivity > 60000) {
        resetTimer();
      }
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, resetTimer]);

  // Catch up after background-tab timer throttling: expire immediately if overdue
  useEffect(() => {
    if (!enabled) return;

    const checkExpiration = () => {
      if (
        document.visibilityState === 'visible' &&
        expirationTimeRef.current &&
        isSessionExpired(expirationTimeRef.current) &&
        !isExpiringRef.current
      ) {
        void handleExpire();
      }
    };

    document.addEventListener('visibilitychange', checkExpiration);
    window.addEventListener('focus', checkExpiration);

    return () => {
      document.removeEventListener('visibilitychange', checkExpiration);
      window.removeEventListener('focus', checkExpiration);
    };
  }, [enabled, handleExpire]);

  return {
    extendSession,
    resetTimer,
    expirationTime: expirationTimeRef.current,
  };
}
